import React, { useEffect, useMemo, useState } from 'react'
import { ChevronDown, Menu, Phone, Mail, X, Facebook, MapPin } from 'lucide-react'
import { NavLink, useLocation } from 'react-router-dom'
import { mainMenu } from '../data/navigation.js'
import { ButtonLink, Container } from './atoms.jsx'
import { cn } from '../lib/utils.js'

function isActivePath(pathname, href) {
  return pathname === href || pathname.startsWith(`${href}/`)
}

function DropdownMenu({ label, items, href, pathname, closeMobile }) {
  const active = items.some((item) => isActivePath(pathname, item.href)) || (href && isActivePath(pathname, href))
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(false)
  }, [pathname])

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {href ? (
        <NavLink
          to={href}
          className={cn(
            'inline-flex items-center gap-1 rounded-none px-2 py-1.5 text-[10px] min-[1380px]:text-[11px] 2xl:text-xs font-bold uppercase tracking-wider transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700',
            active ? 'text-forest-700 border-b-2 border-forest-700' : 'text-stone-700 hover:text-forest-700',
          )}
        >
          {label}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </NavLink>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className={cn(
            'inline-flex items-center gap-1 rounded-none px-2 py-1.5 text-[10px] min-[1380px]:text-[11px] 2xl:text-xs font-bold uppercase tracking-wider transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700',
            active ? 'text-forest-700 border-b-2 border-forest-700' : 'text-stone-700 hover:text-forest-700',
          )}
        >
          {label}
          <ChevronDown className={cn('h-3 w-3 transition opacity-60', open && 'rotate-180')} />
        </button>
      )}

      {open ? (
        <div className="absolute left-0 top-full z-50 w-72 rounded-none border border-stone-200 bg-white p-2 shadow-[0_15px_40px_rgba(0,0,0,0.08)]">
          {items.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={closeMobile}
              className={({ isActive }) =>
                cn(
                  'block rounded-none px-4 py-2 text-xs font-semibold transition hover:bg-stone-50',
                  isActive ? 'bg-stone-50 text-forest-700' : 'text-stone-600 hover:text-forest-700',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function DesktopNavLeft({ pathname }) {
  const leftMenu = useMemo(() => mainMenu.slice(0, 4), [])

  return (
    <div className="hidden items-center gap-1 min-[1380px]:gap-2 2xl:gap-3 min-[1380px]:flex justify-start">
      {leftMenu.map((item, index) => {
        if (item.children) {
          return <DropdownMenu key={item.label} label={item.label} items={item.children} href={item.href} pathname={pathname} />
        }

        const active = isActivePath(pathname, item.href)
        return (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              'py-1.5 text-[10px] min-[1380px]:text-[11px] 2xl:text-xs font-bold uppercase tracking-wider transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700',
              index === 0 ? 'pr-2' : 'px-2',
              active ? 'text-forest-700 border-b-2 border-forest-700' : 'text-stone-700 hover:text-forest-700',
            )}
          >
            {item.label}
          </NavLink>
        )
      })}
    </div>
  )
}

function DesktopNavRight({ pathname }) {
  const rightMenu = useMemo(() => mainMenu.slice(4), [])

  return (
    <div className="hidden items-center gap-1 min-[1380px]:gap-2 2xl:gap-3 min-[1380px]:flex justify-end">
      {rightMenu.map((item, index) => {
        if (item.children) {
          return <DropdownMenu key={item.label} label={item.label} items={item.children} href={item.href} pathname={pathname} />
        }

        const active = isActivePath(pathname, item.href)
        return (
          <NavLink
            key={item.href}
            to={item.href}
            className={cn(
              'py-1.5 text-[10px] min-[1380px]:text-[11px] 2xl:text-xs font-bold uppercase tracking-wider transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-700',
              index === rightMenu.length - 1 ? 'pl-2' : 'px-2',
              active ? 'text-forest-700 border-b-2 border-forest-700' : 'text-stone-700 hover:text-forest-700',
            )}
          >
            {item.label}
          </NavLink>
        )
      })}
    </div>
  )
}

function MobileNav({ open, pathname, close }) {
  const groups = useMemo(() => {
    const list = [
      { title: 'Hovedmenu', items: mainMenu.filter((item) => !item.children) }
    ]
    mainMenu.forEach((item) => {
      if (item.children) {
        list.push({ title: item.label, items: item.children })
      }
    })
    return list
  }, [])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/40 backdrop-blur-xs min-[1380px]:hidden" onClick={close}>
      <div
        className="ml-auto flex h-[100dvh] max-h-[100dvh] w-[85%] max-w-sm flex-col overflow-y-auto bg-white p-6 shadow-[0_24px_60px_rgba(0,0,0,0.15)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-stone-100 pb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-forest-700">Helseklinikken</p>
            <p className="text-xs text-stone-500">Gjerrild v/Lars & Jørgen</p>
          </div>
          <button type="button" className="rounded-full p-2 text-stone-700 hover:bg-stone-50" onClick={close} aria-label="Luk menu">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 space-y-6 pb-6">
          {groups.map((group) => (
            <div key={group.title} className="space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400">{group.title}</p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = isActivePath(pathname, item.href)
                  return (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      onClick={close}
                      className={({ isActive }) =>
                        cn(
                          'block rounded-lg px-4 py-2.5 text-sm font-medium transition',
                          isActive || active ? 'bg-forest-50 text-forest-900 font-semibold' : 'text-stone-700 hover:bg-stone-50',
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  )
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-auto space-y-3 border-t border-stone-100 pt-4">
          <a
            href="tel:86384117"
            className="flex items-center justify-center gap-2 rounded-full bg-forest-700 px-4 py-2.5 text-xs font-semibold text-white hover:bg-forest-800 transition"
          >
            <Phone className="h-3.5 w-3.5" /> Ring (+45) 86 38 41 17
          </a>
          <a
            href="tel:23114117"
            className="flex items-center justify-center gap-2 rounded-full bg-forest-700 px-4 py-2.5 text-xs font-semibold text-white hover:bg-forest-800 transition"
          >
            <Phone className="h-3.5 w-3.5" /> Klinik mobil (+45) 23 11 41 17
          </a>
          <a
            href="mailto:info@helselarsen.dk"
            className="flex items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2.5 text-xs font-semibold text-stone-700 hover:bg-stone-50 transition"
          >
            <Mail className="h-3.5 w-3.5" /> Skriv en mail
          </a>
        </div>
      </div>
    </div>
  )
}

export function SiteHeader() {
  const { pathname } = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  return (
    <>
      {/* Top Utility Bar */}
      <div className="w-full bg-forest-600 text-white text-[11px] font-medium py-2.5">
        <div className="w-full px-6 md:px-12 flex flex-col md:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-1">
            <a href="tel:86384117" className="hover:underline flex items-center gap-1.5">
              <Phone className="h-3 w-3" /> Tlf: (+45) 86 38 41 17
            </a>
            <span className="hidden md:inline text-white/40">|</span>
            <a href="tel:23114117" className="hover:underline flex items-center gap-1.5">
              <Phone className="h-3 w-3" /> Klinik mobil: (+45) 23 11 41 17
            </a>
            <span className="hidden md:inline text-white/40">|</span>
            <span className="flex items-center gap-1.5 text-white/90">
              <MapPin className="h-3 w-3" /> Dyrehavevej 11, Gjerrild, 8500 Grenå
            </span>
            <span className="hidden md:inline text-white/40">|</span>
            <a href="mailto:info@helselarsen.dk" className="hover:underline flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> info@helselarsen.dk
            </a>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden lg:flex items-center gap-4">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/height=35,quality=95,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/danmarkfsk-copy-e1712904245405.png"
                alt="Sygeforsikring danmark"
                className="h-[35px] w-auto object-contain"
              />
              <a href="https://aku-net.dk/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/height=35,quality=95,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/1-2026-logo-pa-rab-registreret-akupunkta-r_1.png"
                  alt="Praktiserende Akupunktører"
                  className="h-[35px] w-auto object-contain"
                />
              </a>
              <a href="https://www.sygeforsikring.dk/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/height=35,quality=95,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/dk3-1-e1619592376889.png"
                  alt="RAB Godkendt"
                  className="h-[35px] w-auto object-contain"
                />
              </a>
            </div>
            <span className="text-white/90 text-[10px] uppercase tracking-wider hidden sm:inline lg:hidden">
              RAB Godkendt · Medlem af PA Praktiserende Akupunktører
            </span>
            <a
              href="https://www.facebook.com/profile.php?id=100057487268744#"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/80 transition-colors text-sm flex items-center gap-1.5"
              aria-label="Følg os på Facebook"
            >
              <i className="fa-brands fa-facebook text-base" />
              <span className="text-[10px] uppercase tracking-wider font-bold hidden md:inline">Følg os</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Header */}
      <header className="sticky top-0 z-40 border-b border-stone-200 bg-white/95 backdrop-blur-md">
        <div className="w-full px-6 md:px-12 py-5">
          
          {/* Desktop Navigation Layout (min-[1380px] and up) */}
          <div className="hidden min-[1380px]:flex items-center justify-between w-full gap-4">
            {/* Left Column */}
            <div className="flex-1 flex items-center justify-start min-w-0">
              <DesktopNavLeft pathname={pathname} />
            </div>

            {/* Center Column (Logo Block) */}
            <div className="flex-none max-w-[300px] min-[1380px]:max-w-[340px] 2xl:max-w-[400px] w-full flex items-center justify-center">
              <NavLink to="/" className="flex items-center justify-center group shrink-0 max-w-full">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/height=75,quality=95,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/logo2.png"
                  alt="Helseklinikken i Gjerrild"
                  className="h-16 md:h-[75px] max-w-full w-auto object-contain transition-transform duration-300 group-hover:scale-102"
                />
              </NavLink>
            </div>

            {/* Right Column */}
            <div className="flex-1 flex items-center justify-end min-w-0">
              <DesktopNavRight pathname={pathname} />
            </div>
          </div>

          {/* Mobile Navigation Layout (below min-[1380px]) */}
          <div className="flex min-[1380px]:hidden items-center justify-between w-full">
            {/* Left: Logo Block */}
            <NavLink to="/" className="flex items-center group shrink min-w-0 max-w-[calc(100%-56px)]">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/height=90,quality=95,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/logo2.png"
                alt="Helseklinikken i Gjerrild"
                className="h-14 sm:h-16 md:h-20 max-h-[72px] w-auto object-contain max-w-full transition-transform duration-300 group-hover:scale-102"
              />
            </NavLink>

            {/* Right: Hamburger Menu Button */}
            <button
              type="button"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 shadow-xs"
              onClick={() => setMobileOpen(true)}
              aria-label="Åbn menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

        </div>

        <MobileNav open={mobileOpen} pathname={pathname} close={() => setMobileOpen(false)} />
      </header>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <a
          href="https://www.facebook.com/profile.php?id=100057487268744#"
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-stone-700 border border-stone-200 shadow-md hover:bg-stone-50 transition"
          aria-label="Besøg os på Facebook"
        >
          <i className="fa-brands fa-facebook text-lg" />
        </a>
        <a
          href="tel:86384117"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-forest-700 text-white shadow-md hover:bg-forest-800 transition"
          aria-label="Ring til klinikken"
        >
          <Phone className="h-5 w-5" />
        </a>
        <a
          href="mailto:info@helselarsen.dk"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-stone-700 border border-stone-200 shadow-md hover:bg-stone-50 transition"
          aria-label="Skriv til klinikken"
        >
          <Mail className="h-5 w-5" />
        </a>
      </div>
    </>
  )
}

export function SiteFooter() {
  const { pathname } = useLocation()
  const isPatientStoriesPage = pathname.startsWith('/patienthistorier') || pathname.startsWith('/patienthistorie')
  const isSubpage = pathname !== '/' && !isPatientStoriesPage

  return (
    <>
      {isSubpage && <LogoSlider />}
      
      <footer className="bg-forest-900 text-stone-100 border-t border-forest-950">
        <Container className="py-16 md:py-20">
          <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
            
            {/* Column 1: Identity & Certifications */}
            <div className="space-y-5">
              <h3 className="font-sans text-base font-bold uppercase tracking-wider text-white">
                Helseklinikken i Gjerrild
              </h3>
              <p className="text-xs leading-relaxed text-stone-300">
                Vi er <strong>RAB godkendte akupunktører</strong>, hvilket gør at du kan få tilskud fra Sygeforsikringen DANMARK og flere forsikringsselskabers sundhedsforsikring.
              </p>
              <p className="text-xs leading-relaxed text-stone-300">
                Vi var med til at stifte og er nu medlemmer af PA Praktiserende Akupunktører.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=80,quality=90,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/danmarkfsk-copy-e1712904245405.png"
                  alt="Sygeforsikring danmark"
                  className="h-8 w-auto object-contain bg-white/10 p-1 rounded-sm"
                />
                <a href="https://aku-net.dk/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=80,quality=90,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/1-2026-logo-pa-rab-registreret-akupunkta-r_1.png"
                    alt="Praktiserende Akupunktører"
                    className="h-8 w-auto object-contain bg-white/10 p-1 rounded-sm"
                  />
                </a>
                <a href="https://www.sygeforsikring.dk/" target="_blank" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=80,quality=90,format=auto/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/dk3-1-e1619592376889.png"
                    alt="RAB"
                    className="h-8 w-auto object-contain bg-white/10 p-1 rounded-sm"
                  />
                </a>
              </div>
            </div>

            {/* Column 2: Help Links */}
            <div className="space-y-5">
              <h3 className="font-sans text-base font-bold uppercase tracking-wider text-white">
                Hvad kan vi hjælpe med?
              </h3>
              <ul className="space-y-2.5 text-xs text-stone-300">
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/sygdomme-lidelser" className="hover:text-white transition-colors">
                    Sygdomme / lidelser
                  </NavLink>
                </li>
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/sygdomme-lidelser/behandlingsforloeb" className="hover:text-white transition-colors">
                    Behandlingsforløb
                  </NavLink>
                </li>
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/priser" className="hover:text-white transition-colors">
                    Priser
                  </NavLink>
                </li>
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/nyheder" className="hover:text-white transition-colors">
                    Nyheder
                  </NavLink>
                </li>
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/gode-raad" className="hover:text-white transition-colors">
                    Gode råd
                  </NavLink>
                </li>
                <li className="border-b border-white/10 pb-2">
                  <NavLink to="/klinikkens-baggrund" className="hover:text-white transition-colors">
                    Klinikkens baggrund
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Column 3: Contact Information */}
            <div className="space-y-5">
              <h3 className="font-sans text-base font-bold uppercase tracking-wider text-white">
                Kontakt os
              </h3>
              <div className="space-y-3 text-xs text-stone-300">
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-white/60 shrink-0 mt-0.5" />
                  <span>
                    <strong>Helseklinikken i Gjerrild</strong>
                    <br />
                    Dyrehavevej 11, Gjerrild
                    <br />
                    8500 Grenå
                  </span>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-white/60 shrink-0" />
                  <a href="tel:86384117" className="hover:underline">
                    Tlf.: (+45) 86 38 41 17
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-white/60 shrink-0 opacity-0" aria-hidden="true" />
                  <a href="tel:23114117" className="hover:underline">
                    Klinik mobil: (+45) 23 11 41 17
                  </a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-white/60 shrink-0" />
                  <a href="mailto:info@helselarsen.dk" className="hover:underline">
                    info@helselarsen.dk
                  </a>
                </p>
                <p className="text-xs text-stone-300 pt-1">
                  CVR nr.: 20563605
                </p>
              </div>
            </div>

            {/* Column 4: Location Map */}
            <div className="space-y-5">
              <h3 className="font-sans text-base font-bold uppercase tracking-wider text-white">
                Vores beliggenhed
              </h3>
              <div className="w-full h-36 rounded-lg overflow-hidden border border-white/10">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2203.447545638167!2d10.8312345!3d56.4812345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464dd123456789ab%3A0x1234567890abcdef!2sDyrehavevej%2011%2C%20Gjerrild%2C%208500%20Gren%C3%A5!5e0!3m2!1sda!2sdk!4v1700000000000!5m2!1sda!2sdk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="opacity-80 hover:opacity-100 transition-opacity"
                  title="Kort over Helseklinikken i Gjerrild"
                ></iframe>
              </div>
            </div>

          </div>
        </Container>

        {/* Copyright Bar */}
        <div className="bg-forest-950 py-5 text-center text-xs text-stone-300 border-t border-white/5">
          <Container>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
              <p>© 2026 Helseklinikken i Gjerrild v/Lars & Jørgen. Alle rettigheder forbeholdes.</p>
              <div className="flex items-center gap-4">
                <a href="https://www.iubenda.com/privacy-policy/43177838" className="iubenda-white iubenda-noiframe iubenda-embed" title="Privatlivspolitik ">Privatlivspolitik</a>
                <a href="https://www.iubenda.com/privacy-policy/43177838/cookie-policy" className="iubenda-white iubenda-noiframe iubenda-embed" title="Cookiepolitik ">Cookiepolitik</a>
              </div>
            </div>
          </Container>
        </div>
      </footer>
    </>
  )
}

export function LogoSlider() {
  const logos = [
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/danmarkfsk-copy-e1712904245405.png',
      alt: 'Sygeforsikring danmark',
    },
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/1-2026-logo-pa-rab-registreret-akupunkta-r_1.png',
      alt: 'Praktiserende Akupunktører',
      href: 'https://aku-net.dk/',
    },
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/dk3-1-e1619592376889.png',
      alt: 'Registreret Alternativ Behandler (RAB)',
      href: 'https://www.sygeforsikring.dk/',
    },
    {
      src: 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/zhanzhuang.png',
      alt: 'Zhan Zhuang',
      href: '/behandlingsmetoder/chi-kung-energitraening',
    },
  ]

  // Duplicate the logos to ensure seamless looping
  const duplicatedLogos = [...logos, ...logos, ...logos, ...logos, ...logos, ...logos]

  return (
    <div className="border-t border-stone-200 bg-stone-50 py-10">
      <Container>
        <div className="text-center mb-6">
          <p className="text-xs font-bold uppercase tracking-wider text-stone-500">
            Godkendelser, Tilskud & Medlemsskaber
          </p>
        </div>
        <div className="relative w-full overflow-hidden before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-16 before:bg-gradient-to-r before:from-stone-50 before:to-transparent after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-16 after:bg-gradient-to-l after:from-stone-50 after:to-transparent md:before:w-32 md:after:w-32">
          <div className="flex w-max items-center gap-16 md:gap-24 logo-marquee-track py-2">
            {duplicatedLogos.map((logo, index) => {
              const hasLink = !!logo.href;
              const content = (
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=320,quality=90,format=auto/${logo.src.replace('https://assets.auxodev.dk/', '')}`}
                  alt={logo.alt}
                  className={`h-12 md:h-14 w-auto object-contain transition-opacity duration-200 ${
                    hasLink 
                      ? 'opacity-75 hover:opacity-100 cursor-pointer' 
                      : 'opacity-75'
                  }`}
                />
              )

              if (logo.href) {
                if (logo.href.startsWith('/')) {
                  return (
                    <NavLink
                      key={index}
                      to={logo.href}
                      className="block focus:outline-none focus:ring-2 focus:ring-forest-700"
                    >
                      {content}
                    </NavLink>
                  )
                }
                return (
                  <a
                    key={index}
                    href={logo.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block focus:outline-none focus:ring-2 focus:ring-forest-700"
                  >
                    {content}
                  </a>
                )
              }

              return <div key={index}>{content}</div>
            })}
          </div>
        </div>
      </Container>
    </div>
  )
}
